import React, { useMemo } from 'react';
import { Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  extractDiscountInfo,
  getReservationGuestName,
  getReservationTimeValue,
  getStatusMeta,
  isReservationOnDay,
} from './utils';

export default function ReservationTablePlanView({
  tables = [],
  reservations = [],
  displayDate,
  isLoading,
  onSelectReservation,
}) {
  const activeReservations = useMemo(
    () => reservations.filter((reservation) => isReservationOnDay(reservation, displayDate)),
    [reservations, displayDate],
  );

  if (isLoading && !tables.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-md border bg-background">
        <span className="text-sm text-slate-500">Floor Plan wird geladen…</span>
      </div>
    );
  }

  const tablesBySection = tables.reduce((accumulator, table) => {
    const sectionName = table?.section?.name || table?.area?.name || 'Unassigned';
    if (!accumulator[sectionName]) accumulator[sectionName] = [];
    accumulator[sectionName].push(table);
    return accumulator;
  }, {});

  const getTableState = (table) => {
    const reservation = activeReservations.find(
      (entry) => String(entry.table_id || '') === String(table.id || '')
        && ['seated', 'confirmed', 'pending'].includes(String(entry.status || '')),
    );

    if (!reservation) {
      return { status: 'free', reservation: null };
    }

    return {
      status: String(reservation.status || '') === 'seated' ? 'occupied' : 'reserved',
      reservation,
    };
  };

  const statusStyles = {
    free: 'border-dashed border-muted-foreground/30 bg-muted/5 text-muted-foreground hover:border-primary/50 hover:bg-muted/10',
    reserved: 'border-solid border-blue-400 bg-blue-50 text-blue-900 shadow-sm hover:bg-blue-100',
    occupied: 'border-solid border-green-500 bg-green-50 text-green-900 shadow-md ring-1 ring-green-500/20 hover:bg-green-100',
  };

  return (
    <div className="h-full overflow-auto bg-muted/10 p-4">
      {Object.entries(tablesBySection).map(([sectionName, sectionTables]) => (
        <div key={sectionName} className="mb-6 rounded-lg border bg-background p-4 shadow-sm last:mb-0">
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-sm font-bold uppercase tracking-wide text-foreground/80">{sectionName}</h3>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {sectionTables.map((table) => {
              const { status, reservation } = getTableState(table);
              const discountInfo = reservation ? extractDiscountInfo(reservation) : { hasDiscount: false };
              const statusMeta = reservation ? getStatusMeta(reservation.status) : null;

              return (
                <button
                  key={table.id}
                  type="button"
                  onClick={() => reservation && onSelectReservation?.(reservation)}
                  className={cn(
                    'group relative flex aspect-square min-h-[100px] flex-col items-center justify-center rounded-xl border-2 transition-all',
                    statusStyles[status],
                    table.shape === 'round' ? 'rounded-full' : 'rounded-xl',
                  )}
                >
                  <div className="absolute left-0 top-2 w-full text-center">
                    <span className="text-sm font-bold opacity-90">{table.name}</span>
                  </div>

                  <div className="absolute right-2 top-2 flex items-center rounded-full border bg-background/80 px-1 py-0.5 text-[9px] shadow-sm">
                    <Users className="mr-0.5 h-2.5 w-2.5" />
                    {table.max_seats || table.capacity || 4}
                  </div>

                  {reservation ? (
                    <div className="mt-3 w-full px-1 text-center">
                      <div className="mb-0.5 truncate px-1 text-xs font-bold leading-tight">
                        {getReservationGuestName(reservation)}
                      </div>
                      <div className="mb-1 text-[10px] opacity-80">{getReservationTimeValue(reservation)}</div>

                      <div className="flex items-center justify-center gap-1">
                        <div className={cn(
                          'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-medium shadow-sm',
                          statusMeta?.value === 'seated' ? 'bg-green-200 text-green-800' : 'bg-blue-200 text-blue-800',
                        )}>
                          <Users className="h-2.5 w-2.5" />
                          {reservation.party_size || 0}
                        </div>
                        {discountInfo.hasDiscount ? (
                          <div className="flex items-center rounded-sm bg-black px-1 py-0.5 text-[8px] font-bold text-white shadow-sm">
                            -{discountInfo.percentage}%
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-col items-center gap-1 text-sm font-medium text-muted-foreground/40 transition-colors group-hover:text-muted-foreground/60">
                      <span className="text-[10px]">Frei</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
